import React, { useState, useEffect, useId, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";

import { getRuta, validateCedula } from "../../../helpers/Helpers";
import { getToken } from "../../../api/Account";
import { addEmployeeAsync, getPuestosAsync } from "../../../api/Employees";
import {
  EstadoError,
  PageHeader,
  SeccionFormulario,
  useAviso,
} from "../../../components/base";
import {
  CampoFecha,
  CampoMonto,
  CargandoFormularioEmpleado,
  esMontoEscribible,
  llevarAlPrimerError,
  useCargaLocal,
  useEnvio,
  useEscritura,
} from "./comunes";

/**
 * Reglas de validación del alta, las mismas de siempre y en el mismo orden que antes
 * (nombre, apellido, cédula vacía, cédula inválida, puesto, salario vacío y dirección).
 * Antes se mostraba la primera que fallaba; ahora se devuelven todas juntas, cada una con la
 * clave de su campo. El INSS se marca sin obligatoriedad porque nunca se validó, y el salario
 * 0 se acepta como siempre (solo se rechaza el vacío).
 *
 * @returns {Object<string, string>} errores por campo; vacío si todo está bien.
 */
export const validarEmpleado = ({
  firstName,
  lastName,
  identification,
  selectedPuesto,
  salario,
  address,
}) => {
  const errores = {};
  if (firstName.length === 0) errores.firstName = "Ingrese el primer nombre";
  if (lastName.length === 0) errores.lastName = "Ingrese el primer apellido";
  if (identification.length === 0) {
    errores.identification = "Ingrese la cédula";
  } else if (!validateCedula(identification)) {
    errores.identification = "La cédula no es válida. Formato: 000-000000-0000X";
  }
  if (selectedPuesto.length === 0) errores.selectedPuesto = "Seleccione un puesto";
  if (salario.length === 0) errores.salario = "Ingrese el salario";
  if (address.length === 0) errores.address = "Ingrese la dirección";
  return errores;
};

const AddEmployee = () => {
  const token = getToken();
  let ruta = getRuta();
  let navigate = useNavigate();
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const prefijo = useId();
  const formulario = useRef(null);

  const [firstName, setFirstName] = useState("");
  const [secondName, setSecondName] = useState("");
  const [lastName, setLastName] = useState("");
  const [secondLastName, setSecondLastName] = useState("");
  const [identification, setIdentification] = useState("");

  const [puestoList, setPuestoList] = useState([]);
  const [selectedPuesto, setSelectedPuesto] = useState("");

  const [inssNumber, setInssNumber] = useState("");
  const [salario, setSalario] = useState("");
  const [address, setAddress] = useState("");

  const [fechaInicio, setFechaInicio] = useState(new Date());

  // Los errores salen junto a cada campo una vez que se intentó guardar, y se corrigen en vivo.
  const [intentado, setIntentado] = useState(false);
  const errores = intentado
    ? validarEmpleado({ firstName, lastName, identification, selectedPuesto, salario, address })
    : {};

  const { cargando, error, cargar } = useCargaLocal(() => getPuestosAsync(token));

  const getPuestos = async () => {
    const result = await cargar();
    if (result) setPuestoList(result.data.result);
  };

  useEffect(() => {
    getPuestos();
  }, []);

  const addEmployee = () =>
    enviar(async () => {
      setIntentado(true);
      const fallos = validarEmpleado({
        firstName,
        lastName,
        identification,
        selectedPuesto,
        salario,
        address,
      });
      if (Object.keys(fallos).length > 0) {
        llevarAlPrimerError(formulario);
        return;
      }

      const datos = {
        firstName,
        secondName,
        lastName,
        secondLastName,
        address,
        puestoId: selectedPuesto,
        inss: inssNumber,
        identification,
        salario,
        // B17 (se propone aparte): el tipo de contrato va fijo en 1 (CONTRATO FIJO).
        tipoContrato: 1,
        fechaIngreso: moment(fechaInicio).format("YYYY-MM-DD"),
      };

      const ok = await escribir(
        () => addEmployeeAsync(token, datos),
        "No se pudo agregar al empleado"
      );
      if (!ok) return;
      clearData();
      setIntentado(false);
      aviso.exito("Empleado agregado");
    });

  const clearData = () => {
    setFirstName("");
    setSecondName("");
    setLastName("");
    setSecondLastName("");
    setSelectedPuesto("");
    setAddress("");
    setInssNumber("");
    setIdentification("");
    setSalario("");
  };

  //Devuelve un entero positivo
  const funtionSetINSS = (value) => {
    if (/^[0-9]+$/.test(value.toString()) || value === "") {
      setInssNumber(value);
      return;
    }
  };

  const funtionSetSalario = (value) => {
    if (esMontoEscribible(value)) {
      setSalario(value);
    }
  };

  const texto = (campo, etiqueta, valor, alCambiar, extra = {}) => (
    <TextField
      fullWidth
      id={`${prefijo}-${campo}`}
      label={etiqueta}
      value={valor}
      onChange={(e) => alCambiar(e.target.value)}
      error={Boolean(errores[campo])}
      helperText={errores[campo] || extra.ayuda || " "}
      disabled={guardando}
      inputProps={{ autoComplete: "off", ...extra.inputProps }}
      {...extra.props}
    />
  );

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Nuevo empleado"
        subtitulo="Registra sus datos, su puesto y su salario"
        icono={<FontAwesomeIcon icon={faUserPlus} />}
        acciones={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              navigate(`${ruta}/employees`);
            }}
          >
            Regresar
          </Button>
        }
      />

      {error ? (
        <EstadoError
          titulo="No se pudo cargar el formulario"
          mensaje="Faltan los puestos para agregar al empleado. Revisa tu conexión e inténtalo de nuevo."
          onReintentar={getPuestos}
        />
      ) : cargando ? (
        <CargandoFormularioEmpleado etiqueta="Cargando el formulario…" />
      ) : (
        <Stack spacing={3} ref={formulario}>
          <SeccionFormulario titulo="Datos personales">
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                {texto(
                  "firstName",
                  "Primer nombre",
                  firstName,
                  (v) => setFirstName(v.toUpperCase()),
                  { props: { required: true } }
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {texto("secondName", "Segundo nombre", secondName, (v) =>
                  setSecondName(v.toUpperCase())
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {texto(
                  "lastName",
                  "Primer apellido",
                  lastName,
                  (v) => setLastName(v.toUpperCase()),
                  { props: { required: true } }
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {texto("secondLastName", "Segundo apellido", secondLastName, (v) =>
                  setSecondLastName(v.toUpperCase())
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {texto(
                  "identification",
                  "Cédula",
                  identification,
                  (v) => setIdentification(v.toUpperCase()),
                  { ayuda: "Formato 000-000000-0000X", props: { required: true } }
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {texto(
                  "address",
                  "Dirección",
                  address,
                  (v) => setAddress(v.toLocaleUpperCase()),
                  { props: { required: true } }
                )}
              </Grid>
            </Grid>
          </SeccionFormulario>

          <SeccionFormulario titulo="Puesto y salario">
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  required
                  fullWidth
                  id={`${prefijo}-selectedPuesto`}
                  label="Puesto"
                  value={selectedPuesto}
                  onChange={(e) => setSelectedPuesto(e.target.value)}
                  error={Boolean(errores.selectedPuesto)}
                  helperText={errores.selectedPuesto || " "}
                  disabled={guardando}
                >
                  <MenuItem value="">
                    <em>Seleccione un puesto</em>
                  </MenuItem>
                  {puestoList.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.description}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                {texto("inssNumber", "Número INSS", inssNumber, funtionSetINSS, {
                  inputProps: { inputMode: "numeric" },
                })}
              </Grid>
              <Grid item xs={12} md={6}>
                <CampoMonto
                  id={`${prefijo}-salario`}
                  label="Salario"
                  required
                  value={salario}
                  onChange={funtionSetSalario}
                  error={errores.salario}
                  disabled={guardando}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CampoFecha
                  label="Fecha de ingreso"
                  value={fechaInicio}
                  onChange={(newValue) => setFechaInicio(newValue)}
                  disabled={guardando}
                />
              </Grid>
            </Grid>
          </SeccionFormulario>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <Button
              variant="contained"
              size="large"
              disabled={guardando}
              onClick={() => addEmployee()}
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 220 }}
            >
              {guardando ? "Guardando…" : "Agregar empleado"}
            </Button>
          </Box>
        </Stack>
      )}
    </Container>
  );
};

export default AddEmployee;
